/**
 * ⏰ **쉬어 가기 · 밤에는 차분하게** — 보호자가 고르고, 이 기기에서만 센다 (2026-10-24 · 리서치 최종안 ①).
 *
 * ## 근거
 * 서울교육청 2026 「디지털 균형 가이드」(3~4세 하루 1시간 이하 · 신체활동 우선), 영국 정부 2026. 3. 지침
 * (2~4세 하루 1시간 이하 · 잠들기 1시간 전에는 화면을 피함), AAP 2026 의 5C(시간보다 맥락·차분함). 「몇 분」을 정하는 것은
 * 보호자다 — 우리는 **기본값을 끄기**로 두고(지금 화면이 바뀌지 않게) 고를 자리만 준다.
 *
 * ## 무수집
 * 고른 값·센 시간은 이 기기 localStorage(gc_ 접두사 — 「이 기기 저장값 지우기」가 같이 지운다). 서버로 안 간다.
 *
 * 판정은 여기 순수 함수들 — 화면(RestGuard·NightShade·보호자 칸)은 결과만 그린다.
 */

export const REST_CHOICES = [0, 15, 20, 30] as const;
export type RestMinutes = (typeof REST_CHOICES)[number];

export interface RestPrefs {
  /** 0 이면 끔 */
  minutes: RestMinutes;
  /** 저녁 9시 ~ 아침 6시에 화면을 어둡고 따뜻하게 */
  night: boolean;
}
export const DEFAULT_REST: RestPrefs = { minutes: 0, night: false };

export interface RestClock {
  /** 이번 놀이에서 화면이 보인 시간(ms) */
  usedMs: number;
  /** 마지막으로 센 시각(ms) */
  lastTick: number;
}

/** 이만큼 넘게 안 썼으면 「새 놀이」 — 처음부터 센다 */
export const REST_GAP_MS = 10 * 60 * 1000;

/**
 * 한 번 세기. 화면이 보이면 지난번부터 지금까지를 더한다(한 번에 1분 넘게는 안 더한다 — 잠든 사이를 놀이로 세지 않게).
 * 마지막으로 센 지 10분이 넘었으면 처음부터.
 */
export function restTick(c: RestClock | null, now: number, visible: boolean): RestClock {
  if (!c || now - c.lastTick > REST_GAP_MS || now < c.lastTick) return { usedMs: 0, lastTick: now };
  const add = visible ? Math.min(now - c.lastTick, 60_000) : 0;
  return { usedMs: c.usedMs + add, lastTick: now };
}

export function restDue(c: RestClock | null, minutes: RestMinutes): boolean {
  return minutes > 0 && !!c && c.usedMs >= minutes * 60_000;
}

/** 이 기기 시계로 저녁 9시 ~ 아침 6시 */
export function isNight(d: Date): boolean {
  const h = d.getHours();
  return h >= 21 || h < 6;
}

const PREFS_KEY = "gc_rest_v1";
const CLOCK_KEY = "gc_rest_clock_v1";

/** 저장값은 바깥 입력처럼 — 고를 수 있는 값이 아니면 기본값 */
export function loadRestPrefs(): RestPrefs {
  try {
    const v = JSON.parse(localStorage.getItem(PREFS_KEY) ?? "null") as Partial<RestPrefs> | null;
    const minutes = (REST_CHOICES as readonly number[]).includes(Number(v?.minutes)) ? (Number(v!.minutes) as RestMinutes) : 0;
    return { minutes, night: v?.night === true };
  } catch {
    return DEFAULT_REST; // 막힌 저장소·깨진 값 — 끈 채로(지금 화면 그대로)
  }
}

/** @returns 기억했는지 — 못 했으면 보호자 화면이 그렇게 말한다(deviceMode 와 같은 약속) */
export function saveRestPrefs(p: RestPrefs): boolean {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
    return true;
  } catch {
    return false;
  }
}

export function loadRestClock(): RestClock | null {
  try {
    const v = JSON.parse(localStorage.getItem(CLOCK_KEY) ?? "null") as RestClock | null;
    return v && Number.isFinite(v.usedMs) && Number.isFinite(v.lastTick) ? v : null;
  } catch {
    return null;
  }
}

export function saveRestClock(c: RestClock): void {
  try {
    localStorage.setItem(CLOCK_KEY, JSON.stringify(c));
  } catch {
    /* 막힌 저장소 — 이번 창 안에서만 센다(RestGuard 가 상태로도 들고 있다) */
  }
}
